import { gql } from '@apollo/client';

export const FRAGMENT_PROJECT_APPLICATION_DATA = gql`
  fragment projectApplicationData on ProjectApplication {
    id
    refNumber
    title
    status
    isCurrentAppointedEndorser
    hostDepartment {
      id
      abbreviation
      faculty {
        id
        abbreviation
      }
    }
    firstSubmissionDate
    currentEndorsementStep {
      stepSequence
      endorserListingType
    }
    currentAppointedEndorser {
      user {
        id
        name
      }
      role {
        id
        name
      }
      department {
        id
        name
        abbreviation
      }
      faculty {
        id
        name
        abbreviation
      }
    }
    user {
      id
      name
    }
    isResearchOriented
    setting {
      id
      title
      description
      startDate
      endDate
      showPriority
      deadlineOfUsingUpFunding
    }
    team {
      teamMemberType
      name
      department {
        id
        name
      }
      email
      netId
      position
      user {
        id
      }
      userFromHroStaff {
        id
      }
    }
    tdgFund
    matchingFund
    matchingFundFundingSource
    startDate
    endDate
    proposal {
      id
      filename
    }
  }
`;
export const FRAGMENT_ENDORSEMENT_DATA = gql`
  fragment endorsementData on Endorsement {
    endorsementSequence
    stepSequence
    date
    endorsementSteps {
      stepSequence
      endorserListingType
      appointedEndorser {
        user {
          id
          name
        }
        role {
          id
          name
        }
        department {
          id
          name
          abbreviation
        }
        faculty {
          id
          name
          abbreviation
        }
      }
      endorser {
        id
        netId
        name
      }
      endorsed
      comment
      date
    }
  }
`;

export const GET_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  ${FRAGMENT_ENDORSEMENT_DATA}
  query GET_PROJECT_APPLICATION($id: ID!) {
    projectApplication(id: $id) {
      applicationEndorsements {
        endorsements {
          ...endorsementData
        }
        endorsementSequence
      }
      ...projectApplicationData
      currentEndorsement {
        ...endorsementData
      }
    }
  }
`;

export const GET_PROJECT_APPLICATIONS = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  query GET_PROJECT_APPLICATIONS(
    $showPriorities: [Int!]
    $status: [ProjectApplicationStatus!]
    $setting: ID
    $faculty: ID
    $department: ID
    $keyword: String
    $pageSize: Int
    $current: Int
    $sort: ProjectApplicationSort
  ) {
    projectApplications(
      showPriorities: $showPriorities
      status: $status
      setting: $setting
      faculty: $faculty
      department: $department
      keyword: $keyword
      pageSize: $pageSize
      current: $current
      sort: $sort
    ) {
      totalCount
      nodes {
        ...projectApplicationData
      }
    }
  }
`;

export const GET_MY_PROJECT_APPLICATIONS = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  query GET_MY_PROJECT_APPLICATIONS {
    myProjectApplications {
      ...projectApplicationData
    }
  }
`;

export const ADD_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation ADD_PROJECT_APPLICATION($input: AddProjectApplicationInput!) {
    addProjectApplication(input: $input) {
      ...projectApplicationData
    }
  }
`;

export const UPDATE_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation UPDATE_PROJECT_APPLICATION($input: UpdateProjectApplicationInput!) {
    updateProjectApplication(input: $input) {
      ...projectApplicationData
    }
  }
`;
export const UPDATE_PROJECT_APPLICATION_AFTER_ENDORSED = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation UPDATE_PROJECT_APPLICATION_AFTER_ENDORSED(
    $input: UpdateProjectApplicationInput!
  ) {
    updateProjectApplicationAfterEndorsed(input: $input) {
      ...projectApplicationData
    }
  }
`;

export const SUBMIT_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation SUBMIT_PROJECT_APPLICATION($id: ID!) {
    submitProjectApplication(id: $id) {
      ...projectApplicationData
    }
  }
`;

export const ENDORSE_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation ENDORSE_PROJECT_APPLICATION(
    $input: EndorseProjectApplicationInput!
  ) {
    endorseProjectApplication(input: $input) {
      ...projectApplicationData
    }
  }
`;

export const CANCEL_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation CANCEL_PROJECT_APPLICATION($id: ID!) {
    cancelProjectApplication(id: $id) {
      ...projectApplicationData
    }
  }
`;

export const ASSIGN_PROJECT_APPLICATION_ENDORSER = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  ${FRAGMENT_ENDORSEMENT_DATA}
  mutation ASSIGN_PROJECT_APPLICATION_ENDORSER($input: AssignEndorserInput!) {
    assignEndorser(input: $input) {
      ...projectApplicationData
      currentEndorsement {
        ...endorsementData
      }
    }
  }
`;

export const DELETE_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation DELETE_PROJECT_APPLICATION($id: ID!) {
    deleteProjectApplication(id: $id) {
      ...projectApplicationData
    }
  }
`;

export const WITHDRAW_PROJECT_APPLICATION = gql`
  ${FRAGMENT_PROJECT_APPLICATION_DATA}
  mutation WITHDRAW_PROJECT_APPLICATION($id: ID!) {
    withdrawProjectApplication(id: $id) {
      ...projectApplicationData
    }
  }
`;

export const GET_PROJECT_APPLICATION_SUMMARY_LIST = gql`
  query GET_PROJECT_APPLICATION_SUMMARY_LIST(
    $showPriorities: [Int!]
    $status: [ProjectApplicationStatus!]
    $setting: ID
    $faculty: ID
    $department: ID
    $keyword: String
    $pageSize: Int
    $current: Int
    $sort: ProjectApplicationSort
  ) {
    projectApplications(
      showPriorities: $showPriorities
      status: $status
      setting: $setting
      faculty: $faculty
      department: $department
      keyword: $keyword
      pageSize: $pageSize
      current: $current
      sort: $sort
    ) {
      totalCount
      nodes {
        id
        refNumber
        title
        status
        hostDepartment {
          id
          abbreviation
          faculty {
            id
            abbreviation
          }
        }
        user {
          id
          name
          title
          email
        }
        setting {
          id
          title
        }
        team {
          teamMemberType
          name
          department {
            id
            abbreviation
          }
          position
        }
        tdgFund
        matchingFund
        matchingFundFundingSource
        startDate
        endDate
        proposal {
          id
          filename
        }
        firstSubmissionDate
      }
    }
  }
`;

export const BRING_PROJECT_APPLICATION_TO_PROJECTS = gql`
  mutation BRING_PROJECT_APPLICATION_TO_PROJECTS(
    $input: BringProjectApplicationToProjectsInput!
  ) {
    bringProjectApplicationToProjects(input: $input) {
      id
    }
  }
`;
